import clsx from "clsx";
import { useRouter } from "next/router";
import { type ComponentProps, useCallback, useState } from "react";

import { SHARE } from "@shared/config";

import { Button } from "../button";
import { Icon, type IconNameType } from "../icon";
import { Body } from "../typography";

import s from "./social-shared.module.scss";

export type ShareStatus = "idle" | "creating" | "ready" | "copied" | "error";

export type CustomHandlerCopyType = {
  shareUrl: string | null;
  status: ShareStatus;
  createShareUrl: () => Promise<string | null>;
  copyShareUrl: () => Promise<void>;
};

type SupportedSocialPlatform = keyof typeof SHARE;
const supportedSocialPlatforms = Object.keys(
  SHARE,
) as SupportedSocialPlatform[];

export type SharedSocialBaseType = ComponentProps<"div"> & {
  className?: string;
  isSmall?: boolean;
  socilaLinks?: IconNameType[];
  isBigIcons?: boolean;
};

export type SharedSocialCustomType = SharedSocialBaseType & {
  customHandlerCopy: CustomHandlerCopyType;
};

// Хук для общей логики
const useSharedSocialLogic = (
  props: SharedSocialBaseType,
  customHandler?: CustomHandlerCopyType,
) => {
  const { socilaLinks = social, isSmall = false, isBigIcons = false } = props;
  const [isActiveCopy, setActiveCopy] = useState(false);
  const { asPath } = useRouter();

  // Filter social links
  const filterSupportedSocialLinks = (
    links: IconNameType[],
  ): SupportedSocialPlatform[] => {
    return links.filter((link): link is SupportedSocialPlatform =>
      supportedSocialPlatforms.includes(link as SupportedSocialPlatform),
    );
  };

  const filteredSocialLinks = filterSupportedSocialLinks(socilaLinks);

  // Обработка копирования по умолчанию
  const defaultHandleCopyURL = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setActiveCopy(true);

      setTimeout(() => {
        setActiveCopy(false);
      }, 5000);
    } catch {
      console.error("ошибка копирования текста");
    }
  }, []);

  // Используем кастомный обработчик или дефолтный
  const { status } = customHandler || {};

  const handleCopy = customHandler?.copyShareUrl || defaultHandleCopyURL;
  const isCopied = customHandler ? status === "copied" : isActiveCopy;
  const isLoading = customHandler?.status === "creating";

  const getShareUrl = (item: SupportedSocialPlatform) => {
    if (customHandler?.shareUrl) {
      return `${SHARE[item]}${customHandler.shareUrl}`;
    }
    return `${SHARE[item]}${process.env.NEXT_PUBLIC_SITE_URL}${asPath}`;
  };

  return {
    filteredSocialLinks,
    isSmall,
    isBigIcons,
    handleCopy,
    isCopied,
    isLoading,
    shareUrl: customHandler?.shareUrl,
    getShareUrl,
    copyText: customHandler
      ? isCopied
        ? "Ссылка скопирована"
        : isLoading
          ? "Создаю…"
          : ""
      : "Ссылка скопирована",
  };
};

const social: IconNameType[] = ["vk", "tg"];

export const SharedSocial = (
  props: SharedSocialBaseType | SharedSocialCustomType,
) => {
  const isCustomType = (
    props: SharedSocialBaseType | SharedSocialCustomType,
  ): props is SharedSocialCustomType => {
    return (
      "customHandlerCopy" in props && props.customHandlerCopy !== undefined
    );
  };

  const commonProps = useSharedSocialLogic(
    props,
    isCustomType(props) ? props.customHandlerCopy : undefined,
  );

  return (
    <div className={clsx(s.root, props.className)}>
      <Body size="caption" weight="semiBold" className={s.shareTitle}>
        Поделиться
      </Body>

      <div className={clsx(s.social, { [s.bigIcons]: commonProps.isBigIcons })}>
        <Button onClick={commonProps.handleCopy} className={s.btn}>
          <Icon
            size={44}
            name="link"
            className={clsx(s.socialIcon, {
              [s.active]: commonProps.isCopied || commonProps.isLoading,
            })}
          />
        </Button>

        <Body
          size="caption-small"
          className={clsx(s.copyText, {
            [s.viewText]: commonProps.isCopied,
            [s.smallText]: commonProps.isSmall,
          })}
        >
          {commonProps.copyText}
        </Body>

        {(commonProps?.filteredSocialLinks ?? []).map((item, idx) =>
          item != null ? (
            <Button
              className={s.btn}
              // disabled={commonProps?.shareUrl === null}
              key={item ?? idx}
              href={commonProps?.getShareUrl?.(item)}
              target="_blank"
            >
              <Icon size={44} name={item} className={s.socialIcon} />
            </Button>
          ) : null,
        )}
      </div>
    </div>
  );
};

SharedSocial.displayName = "SharedSocial";
